import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, IconButton, Button, Chip, Backdrop, CircularProgress, TextField, Card, Grid,
    Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, Draw as DrawIcon, Clear as ClearIcon, PersonSearch as PersonSearchIcon, Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import useDictation from '../../../hooks/useDictation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import extEquipmentService from '../../../services/extEquipmentService';
import { AERIAL_LIFT_ITEMS, CRANE_FORKLIFT_ITEMS, FORM_TYPES, MAINTENANCE_SAFETY_ITEMS } from './constants';
import { checklistError, prefillResults } from './checklist';
import AerialLiftForm from './forms/AerialLiftForm';
import CraneForkliftForm from './forms/CraneForkliftForm';
import MaintenanceSafetyForm from './forms/MaintenanceSafetyForm';

export default function ExtEquipmentDailyCheck() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const qrToken = searchParams.get('qrToken');
    const [uuid] = useState(() => searchParams.get('uuid') || generateUUID());
    const userName = localStorage.getItem('employeeFullName') || '';

    const [tagInfo, setTagInfo] = useState(null);
    const [lastInspection, setLastInspection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const sigPadRef = useRef(null);

    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000, prime: true,
    });

    const toggleNotesDictation = async () => {
        if (!dictationSupported) return;
        if (isListening) { stopDictation(); return; }
        await startDictation({
            onResult: (transcript) => {
                if (!transcript) return;
                const cur = form.notes || '';
                const space = cur && !cur.endsWith(' ') ? ' ' : '';
                handleFieldChange('notes', cur + space + transcript);
            },
        });
    };

    const [form, setForm] = useState({
        uuid,
        qrToken,
        checkDate: dayjs().format('YYYY-MM-DD'),
        formType: '',
        results: {},
        operatorName: userName,
        notes: '',
        signature: '',
        submittedByName: userName,
        status: 'draft',
    });

    const saveTimer = useRef(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const existing = await extEquipmentService.loadDailyCheck(uuid);
            if (existing) {
                setForm(prev => ({ ...prev, ...existing, results: existing.results || {} }));
                if (existing.status === 'submitted') setSubmitted(true);
            }
            if (qrToken) {
                try {
                    const [info, inspRes, dailyRes] = await Promise.all([
                        extEquipmentService.getInfo(qrToken),
                        extEquipmentService.getInspections(qrToken),
                        extEquipmentService.getDailyChecks(qrToken),
                    ]);
                    setTagInfo(info);
                    // Форма та же, что у инспекции; поля из наклейки подставляем сами.
                    if (!existing) {
                        setForm(prev => ({
                            ...prev,
                            formType: info.tag?.formType || '',
                            results: prefillResults(info),
                        }));
                    } else if (!existing.formType) {
                        setForm(prev => ({ ...prev, formType: info.tag?.formType || '' }));
                    }
                    // Последняя заполненная форма: новая daily check, иначе старая инспекция.
                    const lastDaily = (dailyRes.data || []).find(c => c.results && Object.keys(c.results).length > 0);
                    if (lastDaily) {
                        setLastInspection({ ...lastDaily, inspectionDate: lastDaily.checkDate, inspectorName: lastDaily.operatorName });
                    } else if (inspRes.data?.length > 0) {
                        setLastInspection(inspRes.data[0]);
                    }
                } catch (e) { console.error(e); }
            }
            setLoading(false);
        })();
    }, [uuid, qrToken]);

    const handleFieldChange = (field, value) => {
        const u = { ...form, [field]: value };
        setForm(u);
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { extEquipmentService.saveDailyCheckDraft(u).catch(console.error); }, 1000);
    };

    const handleResultsChange = (results) => handleFieldChange('results', results);

    const handleSignatureAccept = () => {
        if (!sigPadRef.current) return;
        handleFieldChange('signature', sigPadRef.current.toDataURL());
        setSignatureModalOpen(false);
    };

    const handleSubmit = async () => {
        const incomplete = checklistError(form.formType, form.results || {});
        if (incomplete) {
            Swal.fire({ icon: 'error', title: 'Incomplete', text: incomplete }); return;
        }
        if (!form.signature) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Signature required' }); return;
        }
        try {
            setSaving(true);
            await extEquipmentService.submitDailyCheck({ ...form, status: 'submitted' });
            await extEquipmentService.deleteDailyCheck(uuid);
            Swal.fire({ icon: 'success', title: 'Daily Check Submitted!' });
            navigate(`/operations/ext-equipment/profile?qrToken=${qrToken}`);
        } catch (e) {
            Swal.fire({ icon: 'error', title: 'Error', text: e.response?.data?.error || 'Failed to submit' });
        } finally { setSaving(false); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(-1)} edge="start"><ArrowBackIcon /></IconButton>
                        <Typography variant="h6" fontWeight={600}>Daily Check</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {tagInfo?.tag?.label && <Chip label={tagInfo.tag.label} size="small" sx={{ fontWeight: 700, fontFamily: 'monospace' }} />}
                        <Chip label={submitted ? 'Submitted' : 'Draft'} size="small"
                            sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto' }}>
                <Box sx={{ maxWidth: 800, mx: 'auto', p: 1 }}>
                    {tagInfo?.tag?.linked && (
                        <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                    Equipment: {tagInfo.tag.linkedDescription || tagInfo.tag.label}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, fontSize: '0.8rem' }}>
                                    {tagInfo.tag.equipmentType && <span><strong>Type:</strong> {tagInfo.tag.unitSubType || tagInfo.tag.equipmentType}</span>}
                                    {tagInfo.tag.manufacturer && <span><strong>MFR:</strong> {tagInfo.tag.manufacturer}</span>}
                                    {tagInfo.tag.model && <span><strong>Model:</strong> {tagInfo.tag.model}</span>}
                                    {tagInfo.tag.serialNumber && <span><strong>S/N:</strong> {tagInfo.tag.serialNumber}</span>}
                                    {tagInfo.tag.equipmentNumber && <span><strong>ID:</strong> {tagInfo.tag.equipmentNumber}</span>}
                                    {tagInfo.tag.owner && <span><strong>Owner:</strong> {tagInfo.tag.owner}</span>}
                                    {tagInfo.tag.location && <span><strong>Location:</strong> {tagInfo.tag.location}</span>}
                                    {tagInfo.tag.assignedName && <span><strong>Assigned:</strong> {tagInfo.tag.assignedName}</span>}
                                    {tagInfo.tag.assignedPhone && <span><strong>Phone:</strong> <a href={`tel:${tagInfo.tag.assignedPhone}`} style={{ color: '#1976d2', textDecoration: 'none' }}>{tagInfo.tag.assignedPhone}</a></span>}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Last Inspection Results */}
                    {lastInspection && (
                        <Box sx={{ mb: 0.5, p: 1.5, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>
                                Last Form: {lastInspection.inspectionDate}
                            </Typography>
                                {lastInspection.inspectorName && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        By: {lastInspection.inspectorName} | {lastInspection.submittedAt ? new Date(lastInspection.submittedAt).toLocaleDateString('en-US') : ''}
                                    </Typography>
                                )}
                                {/* Render inspection results by form type */}
                                {lastInspection.results && (() => {
                                    const r = lastInspection.results;
                                    const ft = lastInspection.formType;
                                    const resultColor = (v) => v === 'pass' || v === 'yes' ? '#4caf50' : v === 'fail' || v === 'no' ? '#f44336' : '#9e9e9e';

                                    let items = [];
                                    if (ft === 'aerial_lift') {
                                        items = AERIAL_LIFT_ITEMS.map((label, i) => ({ label, val: r[`item${i+1}`] }));
                                    } else if (ft === 'crane_forklift') {
                                        items = CRANE_FORKLIFT_ITEMS.map((label, i) => ({ label, val: r[`item${i+1}`] }));
                                    } else if (ft === 'maintenance_safety') {
                                        items = MAINTENANCE_SAFETY_ITEMS.map(({ key, label }) => ({ label, val: r[key] }));
                                    }

                                    // Show only items with issues (fail/no/defect) at top, then summary
                                    const failed = items.filter(it => it.val === 'fail' || it.val === 'no');
                                    const passed = items.filter(it => it.val === 'pass' || it.val === 'yes');
                                    const na = items.filter(it => it.val === 'na');

                                    const valLabel = (v) => {
                                        if (v === 'pass') return { text: 'Pass', color: '#4caf50' };
                                        if (v === 'yes') return { text: 'Yes', color: '#4caf50' };
                                        if (v === 'fail') return { text: 'Fail', color: '#f44336' };
                                        if (v === 'no') return { text: 'No', color: '#f44336' };
                                        if (v === 'na') return { text: 'N/A', color: '#9e9e9e' };
                                        return { text: '—', color: '#ccc' };
                                    };

                                    return (
                                        <Box>
                                            <Typography variant="caption" sx={{ color: failed.length > 0 ? '#c62828' : '#4caf50', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                                {failed.length === 0 ? '✓ All items passed' : `${passed.length} passed, ${failed.length} failed${na.length > 0 ? ', ' + na.length + ' N/A' : ''}`}
                                            </Typography>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, maxHeight: 200, overflow: 'auto' }}>
                                                {items.map((it, i) => {
                                                    const vl = valLabel(it.val);
                                                    return (
                                                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.15, borderBottom: '1px solid #f5f5f5', fontSize: '0.7rem' }}>
                                                            <span style={{ fontWeight: 700, color: vl.color, minWidth: 18, textAlign: 'center' }}>{vl.text}</span>
                                                            <span style={{ color: it.val === 'fail' || it.val === 'no' ? '#c62828' : '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.label}</span>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>

                                            {/* Approved/Rejected for crane */}
                                            {ft === 'crane_forklift' && r.approved !== undefined && (
                                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: r.approved ? '#4caf50' : '#c62828', mt: 0.25 }}>
                                                    Result: {r.approved ? 'APPROVED' : 'REJECTED'}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })()}
                        </Box>
                    )}

                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Daily Check</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Grid container rowSpacing={1} columnSpacing={1}>
                                <Grid item xs={6}>
                                    <TextField label="Date" type="date" size="small" fullWidth disabled
                                        InputLabelProps={{ shrink: true }}
                                        value={form.checkDate} />
                                </Grid>
                                <Grid item xs={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField label="Operator *" size="small" fullWidth value={form.operatorName} disabled
                                            InputLabelProps={{ shrink: true }} />
                                        {!submitted && <IconButton onClick={() => setOperatorModalOpen(true)} color="primary" size="small"><PersonSearchIcon /></IconButton>}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ position: 'relative' }}>
                                        <TextField label="Notes / Issues Found" size="small" fullWidth multiline rows={4} disabled={submitted}
                                            placeholder="Describe any issues, defects, or observations..."
                                            value={form.notes} onChange={e => handleFieldChange('notes', e.target.value)} />
                                        {!submitted && dictationSupported && (
                                            <IconButton onClick={toggleNotesDictation} color={isListening ? 'error' : 'primary'}
                                                size="small" sx={{ position: 'absolute', right: 4, top: 4 }}>
                                                {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    {form.formType && (
                        <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                    {FORM_TYPES.find(f => f.value === form.formType)?.label || 'Inspection'}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                                {form.formType === 'aerial_lift' && <AerialLiftForm results={form.results} onChange={handleResultsChange} isReadOnly={submitted} />}
                                {form.formType === 'crane_forklift' && <CraneForkliftForm results={form.results} onChange={handleResultsChange} isReadOnly={submitted} />}
                                {form.formType === 'maintenance_safety' && <MaintenanceSafetyForm results={form.results} onChange={handleResultsChange} isReadOnly={submitted} inspectionDate={form.checkDate} />}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Accordion defaultExpanded sx={{ mb: 0.5, '&.Mui-expanded': { margin: '0 0 4px 0' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: '40px !important', '& .MuiAccordionSummary-content': { margin: '6px 0 !important' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Signature</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            {form.signature ? (
                                <Card variant="outlined" sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Signature</Typography>
                                        {!submitted && <IconButton size="small" onClick={() => handleFieldChange('signature', '')} color="error"><ClearIcon fontSize="small" /></IconButton>}
                                    </Box>
                                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 0.5, backgroundColor: '#fff' }}>
                                        <img src={form.signature} alt="Signature" style={{ width: '100%', maxHeight: 100, objectFit: 'contain' }} />
                                    </Box>
                                </Card>
                            ) : (
                                <Button variant="outlined" startIcon={<DrawIcon />} onClick={() => setSignatureModalOpen(true)}
                                    disabled={submitted} fullWidth>Add Signature</Button>
                            )}
                        </AccordionDetails>
                    </Accordion>

                    {!submitted && (
                        <Button variant="contained" fullWidth size="large" onClick={handleSubmit} disabled={saving}
                            sx={{ mt: 1, mb: 3, py: 1.5, fontWeight: 700, fontSize: '1rem', backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#43a047' } }}>
                            {saving ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Submit Daily Check'}
                        </Button>
                    )}
                </Box>
            </Box>

            <SignatureModalWithAuto open={signatureModalOpen} onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef} onAccept={handleSignatureAccept} employeeName={form.operatorName} />
            <SelectSingleEmployeeModal open={operatorModalOpen} onClose={() => setOperatorModalOpen(false)}
                onSelect={(emp) => { handleFieldChange('operatorName', emp.FullName || emp.fullName || ''); setOperatorModalOpen(false); }} />
            <Backdrop open={saving} sx={{ zIndex: 9999 }}><CircularProgress color="inherit" /></Backdrop>
        </Box>
    );
}
